import React from 'react';
import { Tooltip } from 'antd';
import { DeleteOutlined, StopOutlined, CheckCircleOutlined, SwapOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('DeleteButton');

// Type definitions
interface DeleteButtonProps {
  onDelete?: (e: React.MouseEvent<HTMLElement>) => void;
  visible?: boolean;
  isEdge?: boolean;
  onEnableDownstream?: () => void;
  onDisableDownstream?: () => void;
  onSwapDirection?: () => void;
}

// Wrapper for the node's delete icon
const NodeActionIconWrapper = styled.div`
  position: absolute;
  top: -10px; // Adjust as needed for your node design
  right: -10px; // Adjust as needed for your node design
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 50%;
  padding: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  z-index: 1002; // Higher than wrapper
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  pointer-events: auto; // Ensure pointer events work
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f5f5f5;
    border-color: #ff4d4f;
    transform: scale(1.05);
  }

  &:active {
    background: #ffe7e7;
    border-color: #ff4d4f;
    transform: scale(0.95);
  }

  &.visible {
    opacity: 1;
  }

  .anticon {
    font-size: 12px;
    color: #ff4d4f;
  }
`;

// Container for multiple actions on an edge
const EdgeActionsContainer = styled.div`
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 14px; // Smaller border radius for more compact look
  padding: 4px 8px; // Tighter padding
  cursor: default;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between; // Even distribution of icons
  gap: 4px; // Smaller gap between icons
  height: 100%;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &.visible {
    opacity: 1;
  }

  .action-icon {
    cursor: pointer;
    font-size: 12px; // Smaller icons for more compact design
    color: #595959;
    transition: all 0.2s ease;
    padding: 3px; // Smaller padding
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 18px; // Ensure consistent icon container size
    height: 18px;
    
    &:hover {
      color: #1890ff;
      background: rgba(24, 144, 255, 0.1);
      transform: scale(1.1); // Slight scale on hover
    }
  }
`;

export const DeleteButton: React.FC<DeleteButtonProps> = ({
  onDelete,
  visible,
  isEdge,
  onEnableDownstream,
  onDisableDownstream,
  onSwapDirection,
}) => {
  const handleDirectDelete = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    logger.debug('Delete button clicked');
    if (onDelete) {
      onDelete(e);
    }
  };

  const handleEnable = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    if (onEnableDownstream) onEnableDownstream();
  };

  const handleDisable = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    if (onDisableDownstream) onDisableDownstream();
  };

  if (!isEdge) {
    // For nodes
    return (
      <NodeActionIconWrapper
        className={visible ? 'visible' : ''}
        onClick={handleDirectDelete}
        onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        title="Delete Node"
        data-testid="delete-node"
      >
        <DeleteOutlined />
      </NodeActionIconWrapper>
    );
  }

  // Handler for swap direction
  const handleSwapDirection = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    e.preventDefault();
    if (onSwapDirection) {
      onSwapDirection();
    }
  };

  // For edges
  return (
    // This div will fill the foreignObject in CustomEdge.tsx
    <div
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()} // Stop clicks on the container from selecting the edge
      style={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <EdgeActionsContainer className={visible ? 'visible' : ''}>
        <Tooltip title="Swap Direction">
          <SwapOutlined className="action-icon" onClick={handleSwapDirection} />
        </Tooltip>
        <Tooltip title="Delete Hop">
          <DeleteOutlined className="action-icon" onClick={handleDirectDelete} />
        </Tooltip>
        <Tooltip title="Disable Downstream Hops">
          <StopOutlined className="action-icon" onClick={handleDisable} />
        </Tooltip>
        <Tooltip title="Enable Downstream Hops">
          <CheckCircleOutlined className="action-icon" onClick={handleEnable} />
        </Tooltip>
      </EdgeActionsContainer>
    </div>
  );
};

export default DeleteButton;
